import { computed, onBeforeUnmount, ref } from 'vue'

const CLICK_OUTSIDE_SUPPRESS_MS = 300
const DRAG_THRESHOLD_PX = 5

export const usePreventClickOutsideOnDrag = () => {
  const isDragging = ref(false)
  const ignoreClickOutsideAfterDrag = ref(false)
  const isPointerActive = ref(false)

  const shouldPreventDialogClose = computed(
    () => isDragging.value || ignoreClickOutsideAfterDrag.value,
  )

  let suppressTimeoutId: ReturnType<typeof setTimeout> | undefined
  let pointerStart = { x: 0, y: 0 }

  const clearSuppressTimeout = () => {
    if (suppressTimeoutId !== undefined) {
      clearTimeout(suppressTimeoutId)
      suppressTimeoutId = undefined
    }
  }

  const scheduleIgnoreClickOutsideAfterDrag = () => {
    ignoreClickOutsideAfterDrag.value = true
    clearSuppressTimeout()
    suppressTimeoutId = setTimeout(() => {
      ignoreClickOutsideAfterDrag.value = false
      suppressTimeoutId = undefined
    }, CLICK_OUTSIDE_SUPPRESS_MS)
  }

  const notifyDragStart = () => {
    isDragging.value = true
    ignoreClickOutsideAfterDrag.value = true
  }

  const notifyDragEnd = () => {
    if (isDragging.value) {
      scheduleIgnoreClickOutsideAfterDrag()
    }
    isDragging.value = false
  }

  const detachPointerListeners = () => {
    window.removeEventListener('pointermove', onPointerMove)
    window.removeEventListener('pointerup', onPointerUp)
    window.removeEventListener('pointercancel', onPointerUp)
  }

  const onPointerMove = (event: PointerEvent) => {
    if (!isPointerActive.value) {
      return
    }

    const deltaX = event.clientX - pointerStart.x
    const deltaY = event.clientY - pointerStart.y

    if (Math.hypot(deltaX, deltaY) > DRAG_THRESHOLD_PX) {
      notifyDragStart()
    }
  }

  const onPointerUp = () => {
    if (isPointerActive.value) {
      notifyDragEnd()
    }

    isPointerActive.value = false
    detachPointerListeners()
  }

  const onPointerDown = (event: PointerEvent) => {
    if (event.button !== 0) {
      return
    }

    isPointerActive.value = true
    pointerStart = { x: event.clientX, y: event.clientY }

    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', onPointerUp)
    window.addEventListener('pointercancel', onPointerUp)
  }

  onBeforeUnmount(() => {
    detachPointerListeners()
    clearSuppressTimeout()
  })

  const createClickOutsideHandler = <T extends (...args: never[]) => unknown>(handler: T) => ({
    handler,
    closeConditional: () => !shouldPreventDialogClose.value,
  })

  return {
    onPointerDown,
    createClickOutsideHandler,
  }
}
